// Modal de rotina: sono, trabalho, exercício, refeição, compromisso.
// Recorrente (dias da semana) ou data única. Pode atravessar a meia-noite.

import type { RoutineEvent, RoutineKind } from '@app-types/domain';
import { todayLocalIso } from '@core/dates';
import { ROUTINE_KIND_META } from '@core/routines';
import { routineActions } from '@core/store';
import { Modal } from '@ui/components/Modal';
import { toast } from '@ui/components/Toast';
import { useState } from 'preact/hooks';

const DOW_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const KINDS: RoutineKind[] = ['sleep', 'work', 'exercise', 'meal', 'commitment', 'other'];

const PRESETS: {
  label: string;
  kind: RoutineKind;
  title: string;
  start: string;
  durH: number;
  durM: number;
  days: number[];
}[] = [
  {
    label: '☾ Sono 23h–7h',
    kind: 'sleep',
    title: 'Sono',
    start: '23:00',
    durH: 8,
    durM: 0,
    days: [0, 1, 2, 3, 4, 5, 6],
  },
  {
    label: '⚒ Trabalho 8h–17h',
    kind: 'work',
    title: 'Trabalho',
    start: '08:00',
    durH: 9,
    durM: 0,
    days: [1, 2, 3, 4, 5],
  },
  {
    label: '⚡ Exercício 18h',
    kind: 'exercise',
    title: 'Exercício',
    start: '18:00',
    durH: 1,
    durM: 0,
    days: [1, 3, 5],
  },
];

function fmtMin(m: string): number {
  const [h, mm] = m.split(':').map(Number);
  return (h || 0) * 60 + (mm || 0);
}
function minToHHMM(min: number): string {
  return `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
}

export function RoutineModal(props: {
  open: boolean;
  routine: RoutineEvent | null;
  onClose: () => void;
}) {
  const r = props.routine;
  const isEdit = r !== null;

  const [title, setTitle] = useState(r?.title || '');
  const [kind, setKind] = useState<RoutineKind>(r?.kind || 'work');
  const [recurring, setRecurring] = useState(r ? !r.date : true);
  const [days, setDays] = useState<number[]>(r?.days_of_week || [1, 2, 3, 4, 5]);
  const [date, setDate] = useState(r?.date || todayLocalIso());
  const [startTime, setStartTime] = useState(r ? minToHHMM(r.start_min) : '08:00');
  const [durH, setDurH] = useState(r ? Math.floor(r.duration_min / 60) : 1);
  const [durM, setDurM] = useState(r ? r.duration_min % 60 : 0);

  function applyPreset(p: (typeof PRESETS)[0]) {
    setTitle(p.title);
    setKind(p.kind);
    setRecurring(true);
    setDays(p.days);
    setStartTime(p.start);
    setDurH(p.durH);
    setDurM(p.durM);
  }

  function toggleDay(d: number) {
    setDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));
  }

  function save() {
    const t = title.trim();
    if (!t) {
      toast('Dá um nome pra rotina', 'err');
      return;
    }
    const duration_min = durH * 60 + durM;
    if (duration_min <= 0) {
      toast('Duração precisa ser maior que zero', 'err');
      return;
    }
    if (recurring && days.length === 0) {
      toast('Escolhe pelo menos 1 dia da semana', 'err');
      return;
    }
    const payload = {
      title: t,
      kind,
      days_of_week: recurring ? [...days].sort() : undefined,
      date: recurring ? undefined : date,
      start_min: fmtMin(startTime),
      duration_min,
    };
    if (isEdit && r) {
      routineActions.update(r.id, payload);
      toast('✓ Rotina atualizada', 'ok');
    } else {
      routineActions.add(payload);
      toast('✓ Rotina criada — o plano desvia dela ao regenerar', 'ok', 4000);
    }
    props.onClose();
  }

  const key = r?.id || 'new';
  const crossesMidnight = fmtMin(startTime) + durH * 60 + durM > 24 * 60;

  return (
    <Modal
      key={key}
      open={props.open}
      onClose={props.onClose}
      title={isEdit ? 'Editar rotina' : 'Nova rotina'}
      maxWidth={520}
      actions={
        <>
          {isEdit && r && (
            <button
              class="btn danger"
              onClick={() => {
                if (confirm(`Remover "${r.title}"?`)) {
                  routineActions.remove(r.id);
                  toast('Rotina removida', 'warn');
                  props.onClose();
                }
              }}
            >
              REMOVER
            </button>
          )}
          <button class="btn" onClick={props.onClose}>
            CANCELAR
          </button>
          <button class="btn primary" onClick={save}>
            {isEdit ? 'SALVAR' : 'CRIAR'}
          </button>
        </>
      }
    >
      {!isEdit && (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
          {PRESETS.map((p) => (
            <button key={p.label} type="button" class="btn sm ghost" onClick={() => applyPreset(p)}>
              {p.label}
            </button>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '10px' }}>
        <div class="field">
          <label>Nome</label>
          <input
            class="input"
            value={title}
            placeholder="ex: Trabalho, Academia, Sono"
            onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
          />
        </div>
        <div class="field">
          <label>Tipo</label>
          <select
            class="select"
            value={kind}
            onChange={(e) => setKind((e.target as HTMLSelectElement).value as RoutineKind)}
          >
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {ROUTINE_KIND_META[k].icon} {ROUTINE_KIND_META[k].label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div class="field">
        <label>Quando</label>
        <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
          <button
            type="button"
            class={`btn sm ${recurring ? 'primary' : 'ghost'}`}
            onClick={() => setRecurring(true)}
          >
            ↻ REPETE
          </button>
          <button
            type="button"
            class={`btn sm ${!recurring ? 'primary' : 'ghost'}`}
            onClick={() => setRecurring(false)}
          >
            ◆ DATA ÚNICA
          </button>
        </div>
        {recurring ? (
          <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
            {DOW_LABELS.map((lbl, d) => (
              <button
                key={lbl}
                type="button"
                class={`btn sm ${days.includes(d) ? 'primary' : 'ghost'}`}
                onClick={() => toggleDay(d)}
              >
                {lbl}
              </button>
            ))}
          </div>
        ) : (
          <input
            class="input"
            type="date"
            value={date}
            style={{ maxWidth: '200px' }}
            onInput={(e) => setDate((e.target as HTMLInputElement).value)}
          />
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
        <div class="field">
          <label>Início</label>
          <input
            class="input"
            type="time"
            value={startTime}
            onInput={(e) => setStartTime((e.target as HTMLInputElement).value || '08:00')}
          />
        </div>
        <div class="field">
          <label>Duração (h)</label>
          <input
            class="input"
            type="number"
            min="0"
            max="23"
            value={durH}
            onInput={(e) =>
              setDurH(Math.max(0, Number.parseInt((e.target as HTMLInputElement).value, 10) || 0))
            }
          />
        </div>
        <div class="field">
          <label>Duração (min)</label>
          <input
            class="input"
            type="number"
            min="0"
            max="59"
            value={durM}
            onInput={(e) =>
              setDurM(
                Math.min(
                  59,
                  Math.max(0, Number.parseInt((e.target as HTMLInputElement).value, 10) || 0),
                ),
              )
            }
          />
        </div>
      </div>

      {crossesMidnight && (
        <div
          style={{
            fontFamily: 'var(--f-mono)',
            fontSize: '11px',
            color: 'var(--purple)',
            padding: '8px 12px',
            background: 'rgba(122,95,255,0.08)',
            borderLeft: '3px solid #7a5fff',
            borderRadius: '4px',
          }}
        >
          ☾ atravessa a meia-noite — o pedaço restante aparece no dia seguinte
        </div>
      )}

      <div
        style={{
          marginTop: '12px',
          fontFamily: 'var(--f-mono)',
          fontSize: '10.5px',
          color: 'var(--text-dim)',
          lineHeight: 1.6,
        }}
      >
        ▸ o GERAR PLANO desvia automaticamente desses horários
      </div>
    </Modal>
  );
}
